import "./styles.css";

type TurnstileWidget = string;

interface TurnstileRenderOptions {
  sitekey: string;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible" | "invisible";
  execution?: "render" | "execute";
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
}

interface TurnstileApi {
  render: (container: string | HTMLElement, options: TurnstileRenderOptions) => TurnstileWidget;
  execute: (widget: TurnstileWidget) => void;
  reset: (widget?: TurnstileWidget) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const form = document.querySelector<HTMLFormElement>("#contact-form");
const contactPanel = document.querySelector<HTMLElement>(".contact-panel");
const emailInput = document.querySelector<HTMLInputElement>("#email");
const startedAtInput = document.querySelector<HTMLInputElement>("#started-at");
const submitButton = document.querySelector<HTMLButtonElement>("#submit-button");
const statusNode = document.querySelector<HTMLElement>("#form-status");
const turnstileSlot = document.querySelector<HTMLElement>("#turnstile-slot");

let turnstileWidget: TurnstileWidget | null = null;
let pendingSubmit = false;
let submitted = false;

const setStatus = (message: string, state: "idle" | "success" | "error" = "idle") => {
  if (!statusNode) {
    return;
  }

  statusNode.textContent = message;
  statusNode.dataset.state = state;
};

const setSubmitting = (isSubmitting: boolean) => {
  pendingSubmit = isSubmitting;

  if (submitButton) {
    submitButton.disabled = isSubmitting || submitted;
    submitButton.textContent = isSubmitting ? "Sending" : "Get in Touch";
  }
};

const setSubmitted = (isSubmitted: boolean) => {
  submitted = isSubmitted;

  if (contactPanel) {
    contactPanel.dataset.submitted = isSubmitted ? "true" : "false";
  }

  if (form) {
    form.setAttribute("aria-hidden", String(isSubmitted));
    form.toggleAttribute("inert", isSubmitted);
  }

  if (emailInput) {
    emailInput.disabled = isSubmitted;
  }

  if (submitButton) {
    submitButton.disabled = isSubmitted || pendingSubmit;
  }
};

const resetStartedAt = () => {
  if (startedAtInput) {
    startedAtInput.value = String(Date.now());
  }
};

const isLikelyEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);

const loadTurnstile = async () => {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  if (!siteKey || !turnstileSlot) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("turnstile_load_failed"));
    document.head.append(script);
  });

  if (!window.turnstile) {
    return;
  }

  turnstileWidget = window.turnstile.render(turnstileSlot, {
    sitekey: siteKey,
    theme: "dark",
    size: "invisible",
    execution: "execute",
    callback: (token) => {
      void submitContact(token);
    },
    "error-callback": () => {
      setSubmitting(false);
      setStatus("Something interrupted the signal. Try again or email us directly.", "error");
    },
    "expired-callback": () => {
      window.turnstile?.reset(turnstileWidget ?? undefined);
    }
  });
};

const submitContact = async (turnstileToken: string) => {
  if (!form || !emailInput) {
    return;
  }

  const formData = new FormData(form);
  const email = emailInput.value.trim().toLowerCase();
  const honeypot = String(formData.get("company") ?? "");
  const startedAt = String(formData.get("startedAt") ?? "");

  try {
    const response = await fetch("/api/contact", {
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify({
        email,
        honeypot,
        startedAt,
        turnstileToken
      })
    });

    const result = (await response.json().catch(() => ({}))) as { ok?: boolean };

    if (!response.ok || !result.ok) {
      throw new Error("submission_failed");
    }

    form.reset();
    resetStartedAt();
    window.turnstile?.reset(turnstileWidget ?? undefined);
    setStatus("Received. We'll be in touch.", "success");
    setSubmitted(true);
  } catch {
    window.turnstile?.reset(turnstileWidget ?? undefined);
    setSubmitted(false);
    setStatus("We couldn't receive that. Try again or email us directly.", "error");
  } finally {
    setSubmitting(false);
  }
};

const onSubmit = (event: SubmitEvent) => {
  event.preventDefault();

  if (!emailInput || pendingSubmit) {
    return;
  }

  const email = emailInput.value.trim();

  if (!isLikelyEmail(email)) {
    setStatus("Use a valid email address.", "error");
    emailInput.focus();
    return;
  }

  setSubmitted(false);
  setSubmitting(true);
  setStatus("");

  if (turnstileWidget && window.turnstile) {
    window.turnstile.execute(turnstileWidget);
    return;
  }

  void submitContact("");
};

const drawSignalField = () => {
  const canvas = document.querySelector<HTMLCanvasElement>("#signal-canvas");
  const context = canvas?.getContext("2d");

  if (!canvas || !context) {
    return;
  }

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let width = 0;
  let height = 0;
  let deviceScale = 1;
  let animationFrame = 0;

  const resize = () => {
    deviceScale = Math.min(window.devicePixelRatio || 1, 2);
    width = window.innerWidth;
    height = window.innerHeight;
    canvas.width = Math.floor(width * deviceScale);
    canvas.height = Math.floor(height * deviceScale);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    context.setTransform(deviceScale, 0, 0, deviceScale, 0, 0);
  };

  const draw = (time = 0) => {
    context.clearRect(0, 0, width, height);
    context.globalCompositeOperation = "screen";

    const drift = prefersReducedMotion ? 0 : time * 0.00006;
    const left = width * 0.08;
    const right = width * 0.92;
    const top = height * 0.18;
    const bandGap = Math.max(34, height * 0.055);

    for (let band = 0; band < 9; band += 1) {
      const baseY = top + band * bandGap;
      const fade = 1 - Math.abs(band - 4) / 5;
      const alpha = Math.max(0.018, 0.082 * fade);

      context.beginPath();
      context.lineWidth = band === 4 ? 1.1 : 0.8;
      context.strokeStyle = `rgba(220, 225, 226, ${alpha})`;

      for (let x = left; x <= right; x += 8) {
        const phase = x * 0.008 + band * 1.7 + drift;
        const fine = Math.sin(x * 0.031 - band * 0.9 - drift * 2);
        const envelope = Math.sin((x / width) * Math.PI);
        const amplitude = (Math.sin(phase) * 10 + fine * 4) * envelope * fade;
        const y = baseY + amplitude;

        if (x === left) {
          context.moveTo(x, y);
        } else {
          context.lineTo(x, y);
        }
      }

      context.stroke();
    }

    for (let i = 0; i < 42; i += 1) {
      const x = left + ((right - left) / 42) * i;
      const intensity = 0.012 + (Math.sin(i * 1.9 + drift * 2) + 1) * 0.012;
      const barHeight = height * (0.12 + ((i * 13) % 29) / 210);
      context.fillStyle = `rgba(215, 220, 222, ${intensity})`;
      context.fillRect(x, height * 0.38 - barHeight / 2, 1, barHeight);
    }

    context.globalCompositeOperation = "source-over";

    if (!prefersReducedMotion) {
      animationFrame = window.requestAnimationFrame(draw);
    }
  };

  resize();
  window.addEventListener("resize", resize, { passive: true });
  draw();

  return () => {
    window.cancelAnimationFrame(animationFrame);
    window.removeEventListener("resize", resize);
  };
};

resetStartedAt();
form?.addEventListener("submit", onSubmit);
void loadTurnstile();
drawSignalField();
